import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import SEO from "../components/common/SEO";
import PageHero from "../components/common/PageHero";
import { StaggerGroup } from "../components/common/Reveal";
import IndustryCard from "../components/cards/IndustryCard";
import CTASection from "../components/common/CTASection";
import { useContent } from "../context/ContentContext";
import { industryGroups } from "../data/industries";

export default function Industries() {
  const { industries } = useContent();
  const [group, setGroup] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return industries.filter((ind) => {
      const matchGroup = group === "All" || ind.group === group;
      const matchQuery =
        !q ||
        ind.name.toLowerCase().includes(q) ||
        ind.tagline.toLowerCase().includes(q) ||
        ind.technology.some((t) => t.toLowerCase().includes(q)) ||
        ind.roles.some((r) => r.toLowerCase().includes(q));
      return matchGroup && matchQuery;
    });
  }, [group, query, industries]);

  return (
    <>
      <SEO
        title="Industries We Serve"
        description="Technology, engineering and talent solutions for healthcare, financial services, manufacturing, semiconductor, engineering, logistics, energy and more."
        path="/industries"
      />
      <PageHero
        eyebrow="Industries"
        title="Industries We Serve"
        subtitle="Technology, engineering and talent solutions built around the unique needs of your industry."
        neural
      />

      <section className="px-6 pb-8">
        <div className="mx-auto max-w-7xl">
          {/* Filters */}
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="industry-filters">
              {industryGroups.map((g) => (
                <button
                  key={g}
                  onClick={() => setGroup(g)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    group === g
                      ? "border-sky-400 bg-sky-400 text-ink-900"
                      : "border-white/10 bg-white/5 text-slate-300 hover:border-white/30 hover:text-white"
                  }`}
                  data-testid={`industry-filter-${g.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                >
                  {g}
                </button>
              ))}
            </div>
            <div className="relative w-full lg:w-72">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search industries or roles..."
                className="w-full rounded-full border border-white/10 bg-white/[0.03] py-2.5 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-sky-400/70"
                data-testid="industry-search"
              />
            </div>
          </div>

          <p className="mt-6 text-sm text-slate-500" data-testid="industry-result-count">
            Showing {filtered.length} {filtered.length === 1 ? "industry" : "industries"}
          </p>

          {filtered.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-white/10 bg-ink-800/40 p-12 text-center text-slate-400" data-testid="industry-empty">
              No industries match your search. Try a different term or filter.
            </div>
          ) : (
            <StaggerGroup key={`${group}-${query}`} className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" stagger={0.05}>
              {filtered.map((ind) => (
                <IndustryCard key={ind.slug} industry={ind} />
              ))}
            </StaggerGroup>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
}
